import React, { useState, useEffect } from "react";
import { createUser, fetchCustomers, fetchInternalUsers } from "./api/users";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Alert, AlertDescription } from "../../../components/ui/alert";
import { Checkbox } from "../../../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Customer, Role, User } from "types/settings";
import { MultiSelectCombobox } from "../../../components/ui/multi-select-combobox";

interface CreateUserModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUserCreated: () => void;
  roles: Role[];
}

const CreateUserModal: React.FC<CreateUserModalProps> = ({ 
  open, 
  onOpenChange, 
  onUserCreated, 
  roles 
}) => {
  const [creating, setCreating] = useState<boolean>(false);
  const [createError, setCreateError] = useState<string>("");
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [suggestedInternalUsers, setSuggestedInternalUsers] = useState<{ user_id: string; name: string; email: string }[]>([]);

  const [showCcInput, setShowCcInput] = useState<boolean>(false);
  const [ccInput, setCcInput] = useState<string>("");
  const [formData, setFormData] = useState({
    name: "",
    mobile_no: "",
    email: "",
    role_id: "",
    user_type: "Customer",
    cust_ids: [] as string[],
    send_onboarding_email: false,
    onboarding_cc: [] as string[],
  });

  useEffect(() => {
    if (open) {
      resetForm();
      loadCustomers();
      loadInternalUsers();
    }
  }, [open]);

  const loadCustomers = async () => {
    try {
      const res = await fetchCustomers();
      setCustomers(res);
    } catch (err) {
      console.error("Failed to load customers", err);
    }
  };

  const loadInternalUsers = async () => {
    try {
      const res = await fetchInternalUsers();
      setSuggestedInternalUsers(res || []);
    } catch (err) {
      console.error("Failed to load internal users for CC suggestions", err);
    }
  };

  const resetForm = () => {
    setCreateError("");
    setValidationErrors({});
    setShowCcInput(false);
    setCcInput("");
    setFormData({
      name: "",
      mobile_no: "",
      email: "",
      role_id: "",
      user_type: "Customer",
      cust_ids: [],
      send_onboarding_email: false,
      onboarding_cc: [],
    });
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: ""
      }));
    }

    if (field === "mobile_no" && value) {
      const mobileRegex = /^\d{10}$/;
      if (value.length > 0 && !mobileRegex.test(value)) {
        setValidationErrors(prev => ({
          ...prev,
          mobile_no: value.length !== 10 ? "Must be exactly 10 digits" : "Only numbers allowed"
        }));
      }
    }

    if (field === "email" && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (value.length > 0 && !emailRegex.test(value)) {
        setValidationErrors(prev => ({
          ...prev,
          email: "Please enter a valid email address"
        }));
      }
    }
  };

  // Switching away from Customer hides the checkbox and clears the CC list.
  const handleUserTypeChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      user_type: value,
      cust_ids: [],
      send_onboarding_email: value === "Customer" ? prev.send_onboarding_email : false,
      onboarding_cc: value === "Customer" ? prev.onboarding_cc : [],
    }));
    setShowCcInput(false);
    setCcInput("");
  };

  const addCcAddress = (addressToAdd?: string) => {
    const emailToValidate = (addressToAdd || ccInput).trim();
    if (!emailToValidate) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailToValidate)) {
      setValidationErrors(prev => ({
        ...prev,
        ccInput: "Please enter a valid email address for CC"
      }));
      return;
    }

    if (!formData.onboarding_cc.includes(emailToValidate)) {
      setFormData(prev => ({
        ...prev,
        onboarding_cc: [...prev.onboarding_cc, emailToValidate],
      }));
    }

    setCcInput("");
    setValidationErrors(prev => ({ ...prev, ccInput: "" }));
  };

  const removeCcAddress = (emailToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      onboarding_cc: prev.onboarding_cc.filter(email => email !== emailToRemove),
    }));
  };

  const removeCustomer = (custId: string) => {
    setFormData((prev) => ({
      ...prev,
      cust_ids: prev.cust_ids.filter((id) => id !== custId),
    }));
  };

  const handleCreateUser = async () => {
    try {
      setCreating(true);
      setCreateError("");

      if (!formData.name.trim()) {
        setCreateError("Name is required");
        return;
      }

      if (!formData.mobile_no.trim()) {
        setCreateError("Mobile number is required");
        return;
      }
      const mobileRegex = /^\d{10}$/;
      if (!mobileRegex.test(formData.mobile_no.trim())) {
        setCreateError("Mobile number must be exactly 10 digits");
        return;
      }

      if (!formData.email.trim()) {
        setCreateError("Email is required");
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email.trim())) {
        setCreateError("Please enter a valid email address");
        return;
      }

      if (formData.cust_ids.length === 0) {
        setCreateError("Customer selection is required");
        return;
      }

      if (!formData.role_id) {
        setCreateError("Role is required");
        return;
      }

      const userData = {
        name: formData.name.trim(),
        mobile_no: formData.mobile_no.trim(),
        email: formData.email.trim(),
        role_id: parseInt(formData.role_id),
        user_type: formData.user_type,
        cust_ids: formData.cust_ids,
        send_onboarding_email: formData.user_type === "Customer" ? formData.send_onboarding_email : false,
        onboarding_cc: (formData.user_type === "Customer" && formData.send_onboarding_email) ? formData.onboarding_cc : [],
      };

      await createUser(userData);

      if (onUserCreated) {
        onUserCreated();
      }

      onOpenChange(false);
    } catch (err: any) {
      console.error("Error creating user:", err);
      let errorMessage = "Failed to create user";
      
      if (err?.response?.data?.detail) {
        if (typeof err.response.data.detail === 'string') {
          errorMessage = err.response.data.detail;
        }
        else if (Array.isArray(err.response.data.detail) && err.response.data.detail.length > 0) {
          const firstError = err.response.data.detail[0];
          errorMessage = firstError.msg || "Validation error";
        }
      } else if (err?.response?.status === 400 && err?.response?.data) {
        errorMessage = err.response.data.detail || err.response.data.message || "Bad request";
      } else if (err?.message) {
        errorMessage = err.message;
      }
      setCreateError(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  const isSingleCustomer = formData.user_type !== "Internal";
  const visibleCustomers = customers.filter(
    (c) => Boolean(c.is_demo) === (formData.user_type === "Demo")
  );

  const selectedCustomers = customers.filter((c) =>
    formData.cust_ids.includes(c.cust_id)
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-base font-medium">Create New User</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-1 space-y-4 pb-1">
          {createError && (
            <Alert variant="destructive">
              <AlertDescription>{createError}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="name" className="text-sm font-medium">Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Enter full name"
              autoFocus={false}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="mobile" className="text-sm font-medium">Mobile Number *</Label>
            <Input
              id="mobile"
              value={formData.mobile_no}
              onChange={(e) => handleInputChange('mobile_no', e.target.value)}
              placeholder="Enter 10-digit mobile number"
              maxLength={10}
              className={validationErrors.mobile_no ? "border-red-500 focus:border-red-500" : ""}
            />
            {validationErrors.mobile_no && (
              <p className="text-sm text-red-600">{validationErrors.mobile_no}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium">Email *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="Enter email address"
              className={validationErrors.email ? "border-red-500 focus:border-red-500" : ""}
            />
            {validationErrors.email && (
              <p className="text-sm text-red-600">{validationErrors.email}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>User Type *</Label>
            <Select
              value={formData.user_type}
              onValueChange={handleUserTypeChange}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select user type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Customer">Customer</SelectItem>
                <SelectItem value="Internal">Internal</SelectItem>
                <SelectItem value="Demo">Demo</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>
              {isSingleCustomer ? "Customer *" : "Customers *"}
            </Label>

            <MultiSelectCombobox
              options={visibleCustomers.map(c => ({
                value: c.cust_id,
                label: c.cust_name,
              }))}
              value={formData.cust_ids}
              onValueChange={(values) => {
                if (isSingleCustomer) {
                  setFormData(prev => ({
                    ...prev,
                    cust_ids: values.length ? [values[values.length - 1]] : [],
                  }));
                } else {
                  setFormData(prev => ({
                    ...prev,
                    cust_ids: values,
                  }));
                }
              }}
              placeholder="Select customers"
              searchPlaceholder="Search customers..."
              emptyText="No customers found"
            />
            {selectedCustomers.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {selectedCustomers.map((cust) => (
                  <span
                    key={cust.cust_id}
                    className="inline-flex items-center gap-1 rounded-full bg-muted px-3 py-1 text-sm"
                  >
                    {cust.cust_name}
                    <button
                      type="button"
                      onClick={() => removeCustomer(cust.cust_id)}
                      className="ml-1 text-muted-foreground hover:text-destructive"
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="role" className="text-sm font-medium">Role *</Label>
            <Select value={formData.role_id} onValueChange={(value) => handleInputChange('role_id', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                {roles.map((role) => (
                  <SelectItem key={role.role_id} value={role.role_id.toString()}>
                    {role.role_desc}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {formData.user_type === "Customer" && (
            <div className="space-y-3 mt-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="send_onboarding_email"
                  checked={formData.send_onboarding_email}
                  onCheckedChange={(checked) => {
                    setFormData(prev => ({
                      ...prev,
                      send_onboarding_email: !!checked,
                      onboarding_cc: [],
                    }));
                    setShowCcInput(false);
                    setCcInput("");
                  }}
                />
                <Label htmlFor="send_onboarding_email" className="text-sm font-medium cursor-pointer">
                  Send onboarding email
                </Label>
              </div>

              {formData.send_onboarding_email && (
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="include_cc"
                      checked={showCcInput}
                      onCheckedChange={(checked) => {
                        setShowCcInput(!!checked);
                        if (!checked) {
                          setFormData(prev => ({ ...prev, onboarding_cc: [] }));
                          setCcInput("");
                          if (validationErrors.ccInput) {
                            setValidationErrors(prev => ({ ...prev, ccInput: "" }));
                          }
                        }
                      }}
                    />
                    <Label htmlFor="include_cc" className="text-sm font-medium cursor-pointer">
                      CC (optional)
                    </Label>
                  </div>

                  {showCcInput && (
                    <div className="space-y-2 pl-6">
                      <div className="flex gap-2">
                        <Input
                          id="cc_input"
                          type="email"
                          value={ccInput}
                          onChange={(e) => {
                            setCcInput(e.target.value);
                            if (validationErrors.ccInput) {
                              setValidationErrors(prev => ({ ...prev, ccInput: "" }));
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ',') {
                              e.preventDefault();
                              addCcAddress();
                            }
                          }}
                          placeholder="Type email address and press Enter"
                          className={validationErrors.ccInput ? "border-red-500" : ""}
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => addCcAddress()}
                        >
                          Add
                        </Button>
                      </div>
                      {validationErrors.ccInput && (
                        <p className="text-sm text-red-600">{validationErrors.ccInput}</p>
                      )}

                      {/* Internal user suggestions with real-time autocomplete search */}
                      {(() => {
                        const query = ccInput.trim().toLowerCase();
                        const available = suggestedInternalUsers.filter(u => {
                          if (formData.onboarding_cc.includes(u.email)) return false;
                          if (!query) return true;
                          return u.name.toLowerCase().includes(query) || u.email.toLowerCase().includes(query);
                        });

                        if (available.length === 0) return null;

                        return (
                          <div className="mt-2 text-xs text-muted-foreground">
                            <span className="font-medium text-slate-700">
                              {query ? "Matching Suggestions:" : "Suggestions (Internal / KAM):"}
                            </span>
                            <div className="flex flex-wrap gap-1.5 mt-1">
                              {available.map(u => (
                                <button
                                  key={u.user_id}
                                  type="button"
                                  onClick={() => addCcAddress(u.email)}
                                  className="text-xs bg-slate-100 hover:bg-blue-50 hover:text-blue-700 text-slate-700 px-2 py-1 rounded border border-slate-200 transition-colors"
                                >
                                  + {u.name} ({u.email})
                                </button>
                              ))}
                            </div>
                          </div>
                        );
                      })()}

                      {/* CC Tags list */}
                      {formData.onboarding_cc.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {formData.onboarding_cc.map((ccEmail) => (
                            <span
                              key={ccEmail}
                              className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 px-3 py-1 text-xs font-medium"
                            >
                              {ccEmail}
                              <button
                                type="button"
                                onClick={() => removeCcAddress(ccEmail)}
                                className="ml-0.5 text-blue-500 hover:text-blue-800"
                              >
                                ✕
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleCreateUser} disabled={creating}>
            {creating ? 'Creating...' : 'Create User'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateUserModal;
